<script>
import Product from '@phoenix/mixins/components/common/Product';
import { mapActions, mapGetters } from 'vuex';
import PictureLazy from '@maison/components/common/PictureLazy.vue';
import WishlistButton from '@maison/components/wishlist/WishlistButton';
import { cloneDeep } from 'lodash-es';
import { PhxIcon } from '@phx-styleguides/rdu';

export default {
  components: {
    WishlistButton,
    PictureLazy,
    PhxIcon,
  },

  mixins: [Product],

  props: {
    titleWithCollection: {
      type: String,
      default: '',
    },
    isDark: {
      type: Boolean,
      default: false,
    },
    isHorizontal: {
      type: Boolean,
      default: false,
    },
    info: {
      type: String,
      default: '',
    },
    hoverImage: {
      type: Object,
      default: null,
    },
    trackingList: {
      type: String,
      default: '',
    },
    inSearch: {
      type: Boolean,
      default: false,
    },
    shouldDisplayRemoveFromWishlist: {
      type: Boolean,
      default: false,
    },
    isPriceHidden: {
      type: Boolean,
      default: false,
    },
    labels: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    ...mapGetters('wishlist', ['isInWishlist']),

    productCardClasses() {
      const cardClasses = ['product-card'];

      if (this.isDark) {
        cardClasses.push('product-card--dark');
      }

      if (this.isHorizontal) {
        cardClasses.push('product-card--horizontal');
      }

      if (this.hoverImage !== null) {
        cardClasses.push('product-card--hover-image');
      }

      return cardClasses;
    },

    productCardActionClasses() {
      const cardActionClasses = ['product-card__action'];

      if (this.shouldDisplayAddToWishlist && this.isInWishlist(this.modelReference)) {
        cardActionClasses.push('product-card__action--visible');
      }

      return cardActionClasses;
    },

    linkClasses() {
      return ['h5 text-base expand-target flex-grow'];
    },

    tagClasses() {
      return [
        'tag me-2 mb-2',
        {
          'tag--dark': this.isDark,
        },
      ];
    },

    productTracking() {
      const data = cloneDeep(this.tracking);

      if (this.trackingList !== '') {
        data.list = this.trackingList;
      }

      return JSON.stringify(data);
    },

    currencyCode() {
      return window.Phoenix.currencyCode;
    },
  },

  methods: {
    ...mapActions('wishlist', ['loadWishlistProducts', 'removeProduct']),

    async onRemoveProduct(reference) {
      const removeButton = this.$refs[`remove-reference-${reference}`][0];
      Toolkit.helpers.buttons.addLoadingState(removeButton);
      await this.removeProduct({ reference });
      Toolkit.helpers.buttons.removeLoadingState(removeButton);
    },
  },
};
</script>

<template>
  <div :class="productCardClasses" data-cy="my-account-wishlist-tile">
    <div v-if="labels.length > 0" class="product-card__tag">
      <div v-for="label in labels" :key="label" :class="tagClasses">
        {{ label }}
      </div>
    </div>
    <div :class="productCardActionClasses">
      <button
        v-if="shouldDisplayRemoveFromWishlist"
        :ref="`remove-reference-${modelReference}`"
        class="button button--icon button--light sgphx"
        :title="$t('Wishlist.removeProduct')"
        :data-cy="`wishlist-item-delete-button-${modelReference}`"
        @click="onRemoveProduct(modelReference)"
      >
        <phx-icon icon="close" />
      </button>
      <wishlist-button v-else-if="shouldDisplayAddToWishlist" :reference="modelReference" :tracking="tracking" />
    </div>

    <div class="product-card-media">
      <picture-lazy v-bind="image" img-class="lazyload--fade product-card-media__image" />
      <picture-lazy v-if="hoverImage" v-bind="hoverImage" img-class="lazyload--fade product-card-media__image-hover" />
    </div>

    <div class="product-card__info">
      <a :href="url" :class="linkClasses" :data-tracking="dataTracking ? JSON.stringify(dataTracking) : null">
        <div class="mt-2 text-sm">{{ titleWithCollection }}</div>
        <div class="text-grey-600 mt-2 text-xs">
          {{ sapReference }}
        </div>
        <div v-if="info" class="text-grey-600 mt-4 text-sm">{{ info }}</div>
      </a>

      <div class="text-md text-center font-bold mt-4">
        <product-price
          v-bind="localizedPrice"
          :should-display-from-price="shouldDisplayFromPrice"
          :price-on-demand="false"
        />
      </div>

      <div v-if="shouldDisplayVatInformation" class="text-xs">
        {{ vatInformation }}
      </div>
    </div>
  </div>
</template>
